class CarryHopeShirtBuilder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.frontSelect = this.querySelector('[data-carry-hope-front]');
    this.sizeSelect = this.querySelector('[data-carry-hope-size]');
    this.sizeField = this.querySelector('[data-carry-hope-size-field]');
    this.pocketSelect = this.querySelector('[data-carry-hope-pocket]');
    this.recommendation = this.querySelector('[data-carry-hope-recommendation]');
    this.preview = this.querySelector('[data-carry-hope-preview]');
    this.previewCaption = this.querySelector('[data-carry-hope-caption]');
    this.placementMessage = this.querySelector('[data-carry-hope-placement]');
    this.mediaGallery = this.findMediaGallery();
    this.lastMediaLayer = null;
    this.galleryOverlay = null;
    this.pocketOverlay = null;
    this.frontBadge = null;
    this.backPreview = null;
    this.backShirt = null;
    this.backArtwork = null;
    this.errorBox = this.querySelector('[data-carry-hope-error]');
    this.productFormComponent = this.closest('product-component')?.querySelector('product-form-component') || document.querySelector('product-form-component');
    this.form = this.productFormComponent?.querySelector('form');
    this.addButton = this.productFormComponent?.querySelector('button[name="add"], [ref="addToCartButton"]');

    try {
      this.baseVariants = JSON.parse(this.querySelector('[data-carry-hope-base-variants]').textContent || '[]');
      this.designData = JSON.parse(this.querySelector('[data-carry-hope-design-data]').textContent || '{}');
      this.mockupAssets = JSON.parse(this.querySelector('[data-carry-hope-mockup-assets]')?.textContent || '{}');
      this.pocketMockupAssets = JSON.parse(this.querySelector('[data-carry-hope-pocket-mockup-assets]')?.textContent || '{}');
      this.backMockupAssets = JSON.parse(this.querySelector('[data-carry-hope-back-mockup-assets]')?.textContent || '{}');
      this.baseVariantIds = new Set(this.baseVariants.map((variant) => String(variant.id)));
    } catch (error) {
      this.showError('The design selector could not load. Please refresh the page.');
      return;
    }

    if (!this.form || !this.frontSelect || !this.sizeSelect || !this.pocketSelect) {
      this.showError('The design selector could not connect to this product. Please refresh the page.');
      return;
    }

    this.populateFrontDesigns();
    this.populatePocketDesigns();
    this.frontSelect.addEventListener('change', () => this.handleFrontChange());
    this.sizeSelect.addEventListener('change', () => this.updatePreview());
    this.pocketSelect.addEventListener('change', () => this.updatePreview());
    this.boundLinkedCartSubmit = (event) => {
      const submittedForm = event.target;
      if (!this.isBaseProductForm(submittedForm)) return;
      this.handleSubmit(event, submittedForm);
    };
    this.boundLinkedCartClick = (event) => {
      const button = event.target?.closest?.('button[name="add"], [ref="addToCartButton"]');
      if (!button) return;
      const submittedForm = button.form || button.closest('form');
      if (!this.isBaseProductForm(submittedForm, button)) return;
      this.handleSubmit(event, submittedForm);
    };
    document.addEventListener('submit', this.boundLinkedCartSubmit, true);
    document.addEventListener('click', this.boundLinkedCartClick, true);
    this.closest('product-component')?.addEventListener('change', () => this.scheduleVariantPreviewRefresh());
    this.lastBaseVariantId = String(this.form.querySelector('input[name="id"]')?.value || '');
    this.variantWatchTimer = window.setInterval(() => {
      const currentId = String(this.form.querySelector('input[name="id"]')?.value || '');
      const nextGallery = this.findMediaGallery();
      const galleryChanged = Boolean(nextGallery && nextGallery !== this.mediaGallery);
      if (galleryChanged) {
        if (this.mediaGallery) this.mediaGallery.classList.remove('carry-hope-gallery--paired');
        this.mediaGallery = nextGallery;
      }
      const nextLayer = this.findMainMediaLayer(this.mediaGallery);
      const layerChanged = Boolean(nextLayer && nextLayer !== this.lastMediaLayer);
      if (nextLayer) this.lastMediaLayer = nextLayer;
      const variantChanged = Boolean(currentId && currentId !== this.lastBaseVariantId);
      if (variantChanged) this.lastBaseVariantId = currentId;
      if (variantChanged || galleryChanged || layerChanged) this.scheduleVariantPreviewRefresh();
    }, 200);

    this.suppressAlternateCheckout();
    this.checkoutGuardObserver = new MutationObserver(() => this.suppressAlternateCheckout());
    this.checkoutGuardObserver.observe(document.body, {childList: true, subtree: true});
  }

  isBaseProductForm(form, trigger = null) {
    if (!form) return false;
    if (form === this.form) return true;
    const candidateId = form.querySelector('input[name="id"]')?.value || trigger?.dataset?.variantId || '';
    if (candidateId && this.baseVariantIds.has(String(candidateId))) return true;
    return Boolean(form.closest('sticky-add-to-cart'));
  }

  suppressAlternateCheckout() {
    const productComponent = this.closest('product-component');
    productComponent?.querySelectorAll('shopify-accelerated-checkout, [data-shopify="payment-button"], .shopify-payment-button').forEach((element) => {
      element.hidden = true;
    });
    document.querySelectorAll('sticky-add-to-cart').forEach((element) => {
      element.hidden = true;
    });
  }

  scheduleVariantPreviewRefresh() {
    const refresh = () => {
      this.recommendSize();
      this.updatePreview();
    };
    window.setTimeout(refresh, 80);
    window.setTimeout(refresh, 300);
    window.setTimeout(refresh, 800);
  }

  availableFrontVariants() {
    return (this.designData.frontVariants || []).filter((item) => item.available && item.quantity > 0);
  }

  populateFrontDesigns() {
    const designs = new Map();
    for (const item of this.availableFrontVariants()) {
      if (!designs.has(item.design)) designs.set(item.design, item);
    }
    for (const [design] of designs) {
      const option = document.createElement('option');
      option.value = design;
      option.textContent = design;
      this.frontSelect.appendChild(option);
    }
  }

  populatePocketDesigns() {
    for (const item of (this.designData.pocketVariants || []).filter((variant) => variant.available && variant.quantity > 0)) {
      const option = document.createElement('option');
      option.value = String(item.id);
      option.textContent = item.design;
      option.dataset.design = item.design;
      this.pocketSelect.appendChild(option);
    }
  }

  handleFrontChange() {
    this.clearError();
    const design = this.frontSelect.value;
    this.sizeSelect.innerHTML = '';
    if (!design) {
      this.sizeField.hidden = true;
      this.hideGalleryPreview();
      return;
    }

    const variants = this.availableFrontVariants().filter((item) => item.design === design);
    for (const item of variants) {
      const option = document.createElement('option');
      option.value = String(item.id);
      option.textContent = `${item.size} — ${item.quantity} available`;
      option.dataset.size = item.size;
      option.dataset.design = item.design;
      option.dataset.image = item.image || '';
      this.sizeSelect.appendChild(option);
    }
    this.sizeField.hidden = true;
    this.recommendSize();
    this.updatePreview();
  }

  selectedBaseVariant() {
    const id = this.form.querySelector('input[name="id"]')?.value;
    return this.baseVariants.find((variant) => String(variant.id) === String(id));
  }

  selectedShirtSize() {
    const variant = this.selectedBaseVariant();
    if (!variant) return '';
    if (variant.options.length >= 2) return variant.options[1];
    return variant.options[0] || '';
  }

  selectedShirtColor() {
    const variant = this.selectedBaseVariant();
    return variant?.options?.[0] || '';
  }

  selectedBackMockup() {
    const key = `${String(this.dataset.productHandle || '').toLowerCase()}|${String(this.selectedShirtColor() || '').toLowerCase()}`;
    return this.backMockupAssets?.[key] || '';
  }

  preferenceOrder(shirtSize) {
    const size = String(shirtSize || '').toLowerCase();
    const title = String(this.dataset.productTitle || '').toLowerCase();
    const isYouth = title.includes('youth');
    const isToddler = title.includes('toddler');

    if (isToddler) {
      return size === '2t' || size === '3t'
        ? ['XS', 'Youth S', 'S', 'Youth M', 'M', 'Regular']
        : ['S', 'Youth S', 'XS', 'Youth M', 'M', 'Regular'];
    }
    if (isYouth) {
      if (size === 'xs' || size === 'small') return ['Youth S', 'S', 'XS', 'Youth M', 'M', 'Regular'];
      if (size === 'medium') return ['Youth M', 'M', 'Youth S', 'S', 'L', 'Regular'];
      return ['Youth L', 'L', 'Youth M', 'M', 'XL', 'Regular'];
    }
    if (size === 'small') return ['S', 'M', 'XS', 'Regular', 'L'];
    if (size === 'medium') return ['M', 'L', 'S', 'Regular'];
    if (size === 'large') return ['L', 'M', 'XL', 'Regular'];
    return ['XL', 'L', 'M', 'Regular', 'S'];
  }

  recommendSize() {
    if (!this.frontSelect.value || !this.sizeSelect.options.length) return;
    const shirtSize = this.selectedShirtSize();
    const preferences = this.preferenceOrder(shirtSize);
    let chosen = null;
    for (const preferred of preferences) {
      chosen = Array.from(this.sizeSelect.options).find((option) => option.dataset.size === preferred);
      if (chosen) break;
    }
    if (!chosen) chosen = this.sizeSelect.options[0];
    this.sizeSelect.value = chosen.value;
    this.recommendation.textContent = `Recommended for ${shirtSize || 'this shirt'}: ${chosen.dataset.size}. You can change it.`;
    this.updatePreview();
  }

  findMediaGallery() {
    return this.closest('.product-information')?.querySelector('media-gallery') || document.querySelector('media-gallery');
  }

  findMainMediaLayer(gallery) {
    if (!gallery) return null;
    const visibleContainers = Array.from(gallery.querySelectorAll('.product-media-container'))
      .filter((container) => !container.closest('.dialog-zoomed-gallery'))
      .map((container) => ({container, rect: container.getBoundingClientRect()}))
      .filter(({container, rect}) => {
        const style = window.getComputedStyle(container);
        return rect.width > 20 && rect.height > 20 && style.display !== 'none' && style.visibility !== 'hidden';
      })
      .sort((left, right) => (right.rect.width * right.rect.height) - (left.rect.width * left.rect.height));
    const container = visibleContainers[0]?.container;
    return container?.querySelector('.product-media') || container || null;
  }

  mainMediaLayer() {
    const gallery = this.findMediaGallery();
    if (gallery && gallery !== this.mediaGallery) {
      if (this.mediaGallery) this.mediaGallery.classList.remove('carry-hope-gallery--paired');
      this.mediaGallery = gallery;
    }
    const layer = this.findMainMediaLayer(this.mediaGallery);
    if (layer) this.lastMediaLayer = layer;
    return layer;
  }

  ensureGalleryOverlay() {
    const layer = this.mainMediaLayer();
    if (!layer) return null;
    if (!this.galleryOverlay) {
      this.galleryOverlay = document.createElement('img');
      this.galleryOverlay.className = 'carry-hope-gallery-artwork';
      this.galleryOverlay.loading = 'eager';
    }
    if (this.galleryOverlay.parentElement !== layer) {
      this.galleryOverlay.remove();
      layer.appendChild(this.galleryOverlay);
    }
    return this.galleryOverlay;
  }

  ensurePocketOverlay() {
    const layer = this.mainMediaLayer();
    if (!layer) return null;
    if (!this.pocketOverlay) {
      this.pocketOverlay = document.createElement('img');
      this.pocketOverlay.className = 'carry-hope-gallery-pocket-artwork';
      this.pocketOverlay.loading = 'eager';
    }
    if (this.pocketOverlay.parentElement !== layer) {
      this.pocketOverlay.remove();
      layer.appendChild(this.pocketOverlay);
    }
    return this.pocketOverlay;
  }

  ensureFrontBadge() {
    const layer = this.mainMediaLayer();
    if (!layer) return null;
    if (!this.frontBadge) {
      this.frontBadge = document.createElement('span');
      this.frontBadge.className = 'carry-hope-preview-badge carry-hope-front-badge';
      this.frontBadge.textContent = 'Front';
    }
    if (this.frontBadge.parentElement !== layer) {
      this.frontBadge.remove();
      layer.appendChild(this.frontBadge);
    }
    return this.frontBadge;
  }

  ensureBackPreview() {
    const gallery = this.findMediaGallery();
    if (!gallery) return null;
    if (gallery !== this.mediaGallery) {
      if (this.mediaGallery) this.mediaGallery.classList.remove('carry-hope-gallery--paired');
      this.mediaGallery = gallery;
    }
    if (!this.backPreview) {
      this.backPreview = document.createElement('div');
      this.backPreview.className = 'carry-hope-back-preview';
      const media = document.createElement('div');
      media.className = 'carry-hope-back-preview__media';
      const badge = document.createElement('span');
      badge.className = 'carry-hope-preview-badge';
      badge.textContent = 'Back';
      this.backShirt = document.createElement('img');
      this.backShirt.className = 'carry-hope-back-preview__shirt';
      this.backShirt.loading = 'eager';
      this.backArtwork = document.createElement('img');
      this.backArtwork.className = 'carry-hope-back-preview__artwork';
      this.backArtwork.loading = 'eager';
      media.append(this.backShirt, this.backArtwork, badge);
      this.backPreview.appendChild(media);
    }
    const zoomDialog = gallery.querySelector(':scope > zoom-dialog');
    if (this.backPreview.parentElement !== gallery || (zoomDialog && this.backPreview.nextElementSibling !== zoomDialog)) {
      this.backPreview.remove();
      gallery.insertBefore(this.backPreview, zoomDialog || null);
    }
    gallery.classList.add('carry-hope-gallery--paired');
    return this.backPreview;
  }

  hideGalleryPreview() {
    if (this.galleryOverlay) this.galleryOverlay.remove();
    if (this.pocketOverlay) this.pocketOverlay.remove();
    if (this.frontBadge) this.frontBadge.remove();
    if (this.backPreview) this.backPreview.remove();
    if (this.mediaGallery) this.mediaGallery.classList.remove('carry-hope-gallery--paired');
    if (this.preview) this.preview.hidden = true;
  }

  updatePreview() {
    const selected = this.sizeSelect.selectedOptions[0];
    if (!selected) {
      this.hideGalleryPreview();
      if (this.placementMessage) this.placementMessage.textContent = 'The large design will print on the front.';
      return;
    }

    const transferSize = selected.dataset.size || 'Regular';
    const largeMatch = String(selected.dataset.design || '').match(/Design\s*#\s*(\d+)/i);
    const largeArtwork = largeMatch ? this.mockupAssets[largeMatch[1]] : '';
    const pocket = this.pocketSelect.selectedOptions[0];
    const hasPocket = Boolean(this.pocketSelect.value);

    if (hasPocket) {
      if (this.galleryOverlay) this.galleryOverlay.remove();
      const pocketMatch = String(pocket.dataset.design || pocket.textContent || '').match(/Design\s*#\s*(\d+)/i);
      const pocketArtwork = pocketMatch ? this.pocketMockupAssets[pocketMatch[1]] : '';
      const pocketLayer = pocketArtwork ? this.ensurePocketOverlay() : null;
      if (pocketLayer) {
        pocketLayer.src = pocketArtwork;
        pocketLayer.alt = `${pocket.dataset.design || pocket.textContent} temporarily layered on the front pocket`;
        pocketLayer.style.setProperty('width', '15%', 'important');
        pocketLayer.style.setProperty('max-width', '15%', 'important');
        pocketLayer.style.setProperty('min-width', '0', 'important');
        pocketLayer.style.setProperty('height', 'auto', 'important');
      } else if (this.pocketOverlay) {
        this.pocketOverlay.remove();
      }
      this.ensureFrontBadge();
      const backPreview = this.ensureBackPreview();
      const base = this.selectedBaseVariant();
      if (backPreview && this.backShirt && this.backArtwork) {
        this.backShirt.src = this.selectedBackMockup() || base?.image || this.mainMediaLayer()?.querySelector('img')?.currentSrc || '';
        this.backShirt.alt = `Back preview of ${this.dataset.productTitle || 'selected shirt'}`;
        this.backArtwork.src = largeArtwork;
        this.backArtwork.alt = `${selected.dataset.design || ''} temporarily layered on the back of the selected shirt`;
        this.backArtwork.style.width = '45%';
        this.backArtwork.hidden = !largeArtwork;
      }
      if (this.placementMessage) this.placementMessage.textContent = 'Front preview: pocket design • Back preview: large shirt design';
      this.previewCaption.textContent = `Front pocket: ${pocket.dataset.design || pocket.textContent} • Back: ${selected.dataset.design || ''}`;
    } else {
      if (this.pocketOverlay) this.pocketOverlay.remove();
      if (this.frontBadge) this.frontBadge.remove();
      if (this.backPreview) this.backPreview.remove();
      if (this.mediaGallery) this.mediaGallery.classList.remove('carry-hope-gallery--paired');
      const overlay = largeArtwork ? this.ensureGalleryOverlay() : null;
      if (overlay) {
        overlay.src = largeArtwork;
        overlay.alt = `${selected.dataset.design} temporarily layered on the front of the selected shirt`;
        overlay.style.width = '25%';
      } else if (this.galleryOverlay) {
        this.galleryOverlay.remove();
      }
      if (this.placementMessage) this.placementMessage.textContent = 'The large design will print on the front.';
      this.previewCaption.textContent = `Front: ${selected.dataset.design || ''}`;
    }
    this.preview.hidden = false;
  }

  disconnectedCallback() {
    if (this.variantWatchTimer) window.clearInterval(this.variantWatchTimer);
    if (this.checkoutGuardObserver) this.checkoutGuardObserver.disconnect();
    if (this.boundLinkedCartSubmit) document.removeEventListener('submit', this.boundLinkedCartSubmit, true);
    if (this.boundLinkedCartClick) document.removeEventListener('click', this.boundLinkedCartClick, true);
    if (this.galleryOverlay) this.galleryOverlay.remove();
    if (this.pocketOverlay) this.pocketOverlay.remove();
    if (this.frontBadge) this.frontBadge.remove();
    if (this.backPreview) this.backPreview.remove();
    if (this.mediaGallery) this.mediaGallery.classList.remove('carry-hope-gallery--paired');
  }

  quantity() {
    const input = this.form.querySelector('input[name="quantity"]');
    const quantity = Number(input?.value || this.productFormComponent?.dataset.quantityDefault || 1);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  async handleSubmit(event, submittedForm = this.form) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.submitting) return;
    this.clearError();

    const base = this.selectedBaseVariant();
    const frontOption = this.sizeSelect.selectedOptions[0];
    if (!base?.id || !base.available) {
      this.showError('Please choose an available shirt color and size.');
      return;
    }
    if (!this.frontSelect.value || !frontOption?.value) {
      this.showError('Please choose a front design.');
      this.frontSelect.focus();
      return;
    }

    const quantity = this.quantity();
    const token = `carry-hope-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const pocketOption = this.pocketSelect.selectedOptions[0];
    const pocketId = this.pocketSelect.value;
    const visibleProperties = pocketId
      ? {
          'Back design': this.frontSelect.value,
          'Front pocket design': pocketOption.dataset.design || pocketOption.textContent,
        }
      : {
          'Front design': this.frontSelect.value,
        };

    const items = [
      {
        id: Number(base.id),
        quantity,
        properties: {
          ...visibleProperties,
          _transfer_size: frontOption.dataset.size || '',
          _carry_hope_group: token,
          _carry_hope_role: 'shirt',
        },
      },
      {
        id: Number(frontOption.value),
        quantity,
        properties: {
          _carry_hope_group: token,
          _carry_hope_role: 'large-design',
          _placement: pocketId ? 'Back' : 'Front',
          _paired_shirt: this.dataset.productTitle || '',
        },
      },
    ];
    if (pocketId) {
      items.push({
        id: Number(pocketId),
        quantity,
        properties: {
          _carry_hope_group: token,
          _carry_hope_role: 'pocket-design',
          _placement: 'Front pocket',
          _paired_shirt: this.dataset.productTitle || '',
        },
      });
    }

    const button = event.submitter || event.target?.closest?.('button[name="add"], [ref="addToCartButton"]') || submittedForm?.querySelector('button[name="add"], [ref="addToCartButton"]') || this.addButton;
    this.submitting = true;
    if (button) button.disabled = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({items}),
        credentials: 'same-origin',
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this shirt.');
      window.location.assign(`${root}cart`);
    } catch (error) {
      this.submitting = false;
      this.showError(error.message || 'Unable to add this shirt. Please try again.');
      if (button) button.disabled = false;
    }
  }

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = false;
  }

  clearError() {
    if (!this.errorBox) return;
    this.errorBox.textContent = '';
    this.errorBox.hidden = true;
  }
}

if (!customElements.get('carry-hope-shirt-builder')) {
  customElements.define('carry-hope-shirt-builder', CarryHopeShirtBuilder);
}
